/**
 * homography.ts
 * تحويلات إسقاطية 3×3 (homography) بين فضاء الشاشة وفضاء مركاتور.
 *
 * لماذا إسقاطية لا خطّية (affine)؟ لأن إسقاط مستوٍ على مستوٍ عبر كاميرا منظورية هو
 * تحويل إسقاطي دائماً: مع خريطة مسطّحة (pitch = 0) يتحوّل تلقائياً إلى تحويل خطّي
 * (المركّبة المتجانسة = 1)، ومع الإمالة يبقى صحيحاً تماماً. هذا يجعل مطابقة الجسيمات
 * والذيول للأرض دقيقة في كل حالات السحب/التكبير/التدوير/الإمالة بلا حالات خاصة.
 *
 * التمثيل: مصفوفة صفّية [a,b,c, d,e,f, g,h,i] بمعنى
 *   [ a b c ]
 *   [ d e f ]
 *   [ g h i ]
 * والتطبيق: p' = (a·x + b·y + c, d·x + e·y + f) ÷ (g·x + h·y + i).
 */

export type Mat3 = [number, number, number, number, number, number, number, number, number];

export const IDENTITY3: Mat3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];

/**
 * التحويل الذي ينقل المربّع الواحد إلى رباعي اعتباطي (Heckbert):
 * (0,0)→p0، (1,0)→p1، (1,1)→p2، (0,1)→p3.
 */
export function squareToQuad(
    p0: readonly [number, number],
    p1: readonly [number, number],
    p2: readonly [number, number],
    p3: readonly [number, number],
): Mat3 {
    const [x0, y0] = p0;
    const [x1, y1] = p1;
    const [x2, y2] = p2;
    const [x3, y3] = p3;

    const sx = x0 - x1 + x2 - x3;
    const sy = y0 - y1 + y2 - y3;

    // رباعي متوازي الأضلاع → التحويل خطّي بالضبط (لا حاجة لحلّ المركّبة المتجانسة).
    if (Math.abs(sx) < 1e-12 && Math.abs(sy) < 1e-12) {
        return [x1 - x0, x3 - x0, x0, y1 - y0, y3 - y0, y0, 0, 0, 1];
    }

    const dx1 = x1 - x2, dx2 = x3 - x2;
    const dy1 = y1 - y2, dy2 = y3 - y2;
    const den = dx1 * dy2 - dx2 * dy1;
    if (!Number.isFinite(den) || Math.abs(den) < 1e-15) return IDENTITY3.slice() as Mat3;

    const g = (sx * dy2 - dx2 * sy) / den;
    const h = (dx1 * sy - sx * dy1) / den;

    return [
        x1 - x0 + g * x1, x3 - x0 + h * x3, x0,
        y1 - y0 + g * y1, y3 - y0 + h * y3, y0,
        g, h, 1,
    ];
}

/** معكوس المصفوفة (عبر المرافقة). يعيد الوحدة إذا كانت شاذّة. */
export function invert3(m: Mat3): Mat3 {
    const [a, b, c, d, e, f, g, h, i] = m;
    const A = e * i - f * h;
    const B = f * g - d * i;
    const C = d * h - e * g;
    const det = a * A + b * B + c * C;
    if (!Number.isFinite(det) || Math.abs(det) < 1e-18) return IDENTITY3.slice() as Mat3;
    const s = 1 / det;
    return [
        A * s, (c * h - b * i) * s, (b * f - c * e) * s,
        B * s, (a * i - c * g) * s, (c * d - a * f) * s,
        C * s, (b * g - a * h) * s, (a * e - b * d) * s,
    ];
}

/** حاصل الضرب a·b (يُطبَّق b أولاً ثم a). */
export function multiply3(a: Mat3, b: Mat3): Mat3 {
    const out = new Array(9) as Mat3;
    for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
            out[r * 3 + c] =
                a[r * 3 + 0] * b[0 * 3 + c] +
                a[r * 3 + 1] * b[1 * 3 + c] +
                a[r * 3 + 2] * b[2 * 3 + c];
        }
    }
    return out;
}

/** يحوّل إلى ترتيب الأعمدة الذي يتوقّعه uniformMatrix3fv. */
export function toColumnMajor(m: Mat3, out = new Float32Array(9)): Float32Array {
    out[0] = m[0]; out[1] = m[3]; out[2] = m[6];
    out[3] = m[1]; out[4] = m[4]; out[5] = m[7];
    out[6] = m[2]; out[7] = m[5]; out[8] = m[8];
    return out;
}

/** هل كل عناصر المصفوفة منتهية (حماية من زوايا غير قابلة للإسقاط عند الإمالة القصوى)؟ */
export function isFinite3(m: Mat3): boolean {
    for (let i = 0; i < 9; i++) if (!Number.isFinite(m[i])) return false;
    return true;
}
